import React, { useState } from 'react';

import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Button, Checkbox, Divider } from '@mui/material';

import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';
import { LbFormControlLabel } from '@infoods/lifebase-ui';

import classes from './CheckoutForm.module.scss';
import { PaymentButton } from '../../../../components/checkout/PaymentButton/PaymentButton';
import { paymentElementsOptions } from '../../../../constants/paymentElementsOptions';
import { TermsLinks } from '../../../../../shared/components/TermsLinks/TermsLinks';
import { getPriceView } from '../../../../../shared/utils/stringUtils';
import { getRouteParam } from '../../../../../shared/utils/angularUtils';
import { generatePaymentResultUrl } from '../../../../utils/generatePaymentResultUrl/generatePaymentResultUrl';
import { checkoutStore } from '../../../../stores/checkoutStore';
import { apiStatusStore } from '../../../../../shared/stores/apiStatusStore';
import { checkoutQueryParams } from '../../../../constants/pathQueryParams';
import { SOLUTION_TYPE } from '../../../../../shared/constants/user-info/solutionType';
import { SUBSCRIPTION } from '../../../../../shared/constants/user-info/subscription';

const CheckoutForm = observer(() => {
  const [termsAccepted, setTermsAccepted] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const retryPath = getRouteParam(checkoutQueryParams.retryPath);
  const paymentResultType = getRouteParam(checkoutQueryParams.paymentResultType);
  const consultationType = getRouteParam(checkoutQueryParams.consultationType);
  const entityId = getRouteParam(checkoutQueryParams.entityId);
  const challengeId = getRouteParam(checkoutQueryParams.challengeId);

  const { checkoutInfo, subscriptionTypeFlags, billingPeriodTypeFlags } = checkoutStore;
  const { loadingStart, loadingEnd } = apiStatusStore;

  if (!checkoutInfo) {
    return null;
  }

  const { paymentIntent, checkoutDetails, subscription } = checkoutInfo;

  const solution =
    subscription.subscriptionType === SUBSCRIPTION.SCAN_IQ
      ? SOLUTION_TYPE.SCAN_IQ
      : SOLUTION_TYPE.NUTRITION;

  const returnUrl = generatePaymentResultUrl(solution, {
    retryPath,
    paymentResultType,
    connectedAccountId: paymentIntent.connectedAccountId,
    consultationType,
    entityId,
    challengeId,
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    loadingStart(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (error) {
      enqueueSnackbar(error.message || 'Something went wrong. Please try again later', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }

    loadingEnd(true);
  };

  const handleChangeTermsAccepted = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setTermsAccepted(checked);
  };

  const getTermsAcceptedLabel = () => {
    if (subscriptionTypeFlags.REGULAR) {
      return `I understand that this is a recurring subscription and I'm going to be billed ${getPriceView(
        subscription.monthlyRenewalPriceFinal,
      )} amount next month (plus taxes) after initial plan ends`;
    }

    if (subscriptionTypeFlags.SCAN_IQ) {
      return 'I understand that I will be billed according to the payment type/subscription that I chose. Subscriptions are recurring and I will be billed until I notify this InBody testing location of my intent to cancel said subscription.';
    }

    return `I understand that this is a recurring subscription and I'm going to be billed ${getPriceView(
      subscription.monthlyRenewalPriceFinal,
    )} amount next month (plus taxes)`;
  };

  const paymentButtonProps = {
    returnUrl,
    label: subscription.name,
    totalAmount: checkoutDetails.totalAmount,
    clientSecret: paymentIntent.clientSecret,
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <PaymentButton type="googlePay" {...paymentButtonProps} />
      <PaymentButton
        type="applePay"
        {...paymentButtonProps}
        className={classes.applePayButton}
      />
      <Divider className={classes.divider} flexItem textAlign="center">
        Or pay with card
      </Divider>
      <PaymentElement
        id="card-info-box" // A&A test id
        options={paymentElementsOptions}
      />
      <Button
        disabled={!termsAccepted && !billingPeriodTypeFlags.DAILY}
        variant="contained"
        type="submit"
        fullWidth
      >
        Pay {getPriceView(checkoutDetails.totalAmount)}
      </Button>
      <div className={classes.terms}>
        {!billingPeriodTypeFlags.DAILY && (
          <LbFormControlLabel
            align="start"
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={handleChangeTermsAccepted}
                color="primary"
                name="saveCard"
              />
            }
            label={getTermsAcceptedLabel()}
          />
        )}
        <TermsLinks />
      </div>
    </form>
  );
});

export { CheckoutForm };
